import { Box, Button, TextField, Typography, styled } from "@mui/material";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { ReactElement, useCallback } from "react";
import { FormattedMessage } from "react-intl";

import { GetActiveCampaignContactForTexterQuery } from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import { CampaignForTexter } from "~frontend/components/threads/ThreadControls/types";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import formatContactName from "~frontend/util/formatContactName";

import { Assignment } from "./types";

const Header = styled(Box)({
  fontSize: "14px",
  fontWeight: 700,
  marginBottom: "4px",
});

const Label = styled(Box)({
  fontSize: "12px",
  fontWeight: 600,
});

const DetailText = styled("span")({
  fontSize: "12px",
  fontWeight: "normal",
});

export default function AssignmentContactDetails({
  assignment,
  contact,
  campaign,
  handleSaveNote,
  mutationPending,
  unsavedContactNote,
  onChangeUnsavedContactNote,
}: {
  assignment: Assignment;
  contact: GetActiveCampaignContactForTexterQuery["getCampaignContact"];
  campaign: CampaignForTexter;
  handleSaveNote: () => void;
  mutationPending: boolean;
  unsavedContactNote: string | null;
  onChangeUnsavedContactNote: (val: string) => void;
}): ReactElement {
  const intl = useIntl();
  const snackbar = useSnackbar();
  const { subaccountLink } = useSubaccount()!;

  const relativeLink = `/threads?thread=${assignment.threadId}&campaign_ids=${campaign.id}&context=${campaign.id}`;

  const link = `${document.location.protocol}//${
    document.location.host
  }${subaccountLink(relativeLink)}`;

  const handleCopyLink = useCallback(async () => {
    await navigator.clipboard.writeText(link);
    snackbar.enqueueSnackbar({
      message: intl.formatMessage({
        defaultMessage: "Copied to clipboard!",
        description: "Message when link was copied to keyboard",
      }),
    });
  }, [link, snackbar, intl]);

  return (
    <FlexBox>
      <Box sx={{ flex: 1 }}>
        <Header sx={{ fontSize: "14px", fontWeight: 700, marginBottom: "4px" }}>
          <FormattedMessage
            defaultMessage="Contact Details"
            description="Header for the panel of contact details in the thread controls"
          />
        </Header>
        <Label sx={{ fontSize: "12px", fontWeight: 600 }}>
          {formatContactName(
            {
              firstName: assignment.contactFirstName,
              lastName: assignment.contactLastName,
            },
            { formatMessage: intl.formatMessage }
          )}
        </Label>
        <Header sx={{ marginTop: "24px" }}>
          <FormattedMessage
            defaultMessage="Conversation Link"
            description="Header for the sharable conversation link"
          />
        </Header>
        <Typography variant="body2">
          <FormattedMessage
            defaultMessage="Need to share this conversation with an organizer?"
            description="Explanatory message for conversation link"
          />
          <br />
          <LinkButton onClick={handleCopyLink}>
            <FormattedMessage
              defaultMessage="Click here to copy a link to send to your account admin."
              description="Conversation link copy button label"
            />
          </LinkButton>
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <FlexBox>
          <Header sx={{ flex: 1 }}>
            <FormattedMessage
              defaultMessage="Conversation Notes"
              description="Header for the conversation notes input"
            />
          </Header>

          <Box>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSaveNote}
              disabled={mutationPending || unsavedContactNote == null}
            >
              Save
            </Button>
          </Box>
        </FlexBox>
        <TextField
          sx={{ paddingTop: "10px" }}
          multiline
          minRows={3}
          value={unsavedContactNote ?? contact.note?.text ?? ""}
          placeholder={intl.formatMessage({
            defaultMessage:
              "Take notes here to capture details about your conversation with this contact.",
            description: "Placeholder text in the Contact Notes input",
          })}
          onChange={e => onChangeUnsavedContactNote(e.target.value)}
          fullWidth
        />
        <Box>
          <DetailText>
            {contact.note?.updatedBy && (
              <>
                Updated by {contact.note.updatedBy.fullName} at{" "}
                {DateTime.fromMillis(contact.note.updatedAt).toLocaleString(
                  DateTime.DATETIME_SHORT
                )}
              </>
            )}
          </DetailText>
        </Box>
      </Box>
    </FlexBox>
  );
}
